import {
    type Improvement,
    ImprovementOptions,
    type MoreLessNoChange,
    MoreLessNoChangeOptions,
    type MoreOrLess,
    MorOrLessOptions,
    type YesNoNa,
    YesNoNaOptions,
} from "@qendo/survey/fields";
import { useEffect } from "react";
import { FieldGroup } from "~/components/form/FieldGroup";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { YesNo } from "~/components/form/radio/YesNo";
import { TextInput } from "~/components/form/TextInput";
import { AnnualSurvey2025Data } from "~/entities/survey/annual-survey2025-data";
import { useProfile } from "~/providers/ProfileProvider";
import type { DataProperty } from "~/util/data";

export type AnnualSurvey2025Props = {
    value?: AnnualSurvey2025Data | null;
    setPayload: (payload: AnnualSurvey2025Data) => void;
    disabled?: boolean;
};

export const AnnualSurvey2025 = ({ value, setPayload, disabled }: AnnualSurvey2025Props) => {
    const current = value ?? new AnnualSurvey2025Data();

    const updateValue = <K extends DataProperty<AnnualSurvey2025Data>>(field: K, value: AnnualSurvey2025Data[K]) => {
        const payload = AnnualSurvey2025Data.from(current);

        payload[field] = value;

        if (payload.dirty(field)) {
            setPayload(payload);
        }
    };

    const { profile, is_primary } = useProfile();

    // biome-ignore lint/correctness/useExhaustiveDependencies(updateValue): initial load
    // biome-ignore lint/correctness/useExhaustiveDependencies(is_primary): initial load
    // biome-ignore lint/correctness/useExhaustiveDependencies(profile.postcode): initial load
    useEffect(() => {
        if (is_primary) {
            updateValue("postcode", profile.postcode ?? "");
        }
    }, []);

    return (
        <FieldGroup>
            <YesNo
                title="Are you a new user of our app this period?"
                name="new_user"
                value={current.new_user}
                onValueChange={(value: boolean) => updateValue("new_user", value)}
                disabled={disabled}
            />
            <TextInput
                label="Where in do you live? Share your post code"
                placeholder="Postcode"
                name="postcode"
                value={current.postcode}
                onChangeText={(value: string) => updateValue("postcode", value)}
                disabled={disabled}
            />
            <TextInput
                label="Help us better understand our users. What’s your ethnicity?"
                name="ethnicity"
                value={current.ethnicity}
                onChangeText={(value: string) => updateValue("ethnicity", value)}
                disabled={disabled}
            />
            <TextInput
                label="What language or languages do you speak at home?"
                name="languages"
                value={current.languages}
                onChangeText={(value: string) => updateValue("languages", value)}
                disabled={disabled}
            />
            <YesNo
                title="Have you had any unplanned visits to a Public Hospital, Emergency or specialist recently?"
                name="unplanned_visits"
                value={current.unplanned_visits}
                onValueChange={(value: boolean) => updateValue("unplanned_visits", value)}
                disabled={disabled}
            />
            <RadioSelector<MoreOrLess>
                title="Are you seeing your GP more or less recently?"
                options={MorOrLessOptions}
                columns={2}
                name="visiting_gp"
                value={current.visiting_gp}
                onValueChange={(value: MoreOrLess) => updateValue("visiting_gp", value)}
                disabled={disabled}
            />
            <RadioSelector<YesNoNa>
                title="If you are employed, have you learnt tools to help support you to work?"
                options={YesNoNaOptions}
                name="employment_tools"
                value={current.employment_tools}
                onValueChange={(value: YesNoNa) => updateValue("employment_tools", value)}
                disabled={disabled}
            />
            <RadioSelector<MoreLessNoChange>
                title="Are you moving your body more or less recently?"
                options={MoreLessNoChangeOptions}
                name="movement"
                value={current.movement}
                onValueChange={(value: MoreLessNoChange) => updateValue("movement", value)}
                disabled={disabled}
            />
            <RadioSelector<Improvement>
                title="Have your eating habits changed recently?"
                options={ImprovementOptions}
                name="eating_habits"
                value={current.eating_habits}
                onValueChange={(value: Improvement) => updateValue("eating_habits", value)}
                disabled={disabled}
            />
            <RadioSelector<Improvement>
                title="How’s your mental health been?"
                options={ImprovementOptions}
                name="mental_health"
                value={current.mental_health}
                onValueChange={(value: Improvement) => updateValue("mental_health", value)}
                disabled={disabled}
            />
            <RadioSelector<Improvement>
                title="How are you managing your daily activities?"
                options={ImprovementOptions}
                name="daily_activities"
                value={current.daily_activities}
                onValueChange={(value: Improvement) => updateValue("daily_activities", value)}
                disabled={disabled}
            />
        </FieldGroup>
    );
};
